<template>
  <div class="my-10">
    <div class="flex justify-between mb-5">
      <h1 class="text-2xl font-bold">
        {{ $t('nav.publications') }}
      </h1>

      <SortButton
        class="bg-white"
        v-if="currentRelevancy"
        @on-select="handleRelevancy"
        :options="SORT_OPTIONS"
        :defaultValue="currentRelevancy"
      />
    </div>

    <div class="grid grid-cols-4 gap-5">
      <div class="col-span-1 max-md:hidden">
        <PublicationSide
          :key="keyVal"
          routeName="publications"
          :currentCategory="currentCategory"
          :currentSubscription="currentSubscription"
          :isVip="isVip"
          :currentSort="currentSort"
          :currentPublished="currentPublished"
          :hideFilters="publicationSideHideFilters"
        >
          <template #dynamic>
            <template v-if="filtersReady">
              <component
                v-for="field in ogProperties"
                class="my-3"
                :is="getComponent(field.data_type, field.is_range_filter)"
                :key="keyVal + '-' + field.id"
                :ref="field.id"
                :field="field"
                :name="field.name"
                :label="field.name"
                :required="false"
                :property="field.id"
                :mode="field.is_range_filter ? 'range' : 'simple'"
                :options="field.values?.results"
                :dataType="field.data_type"
                :hasNext="field.values?.next ? true : false"
                :page="1"
                @valueChanged="getResultCount"
                @rangeValueChanged="setRangeFilter"
              />
            </template>

            <div v-if="categoryDetail?.has_address" class="flex flex-col gap-1 mb-10">
              <label class="font-semibold">{{ $t('page.district') }}</label>
              <Multiselect
                v-model="currentDistrict"
                mode="tags"
                :options="districtsArray.map(d => ({ label: d.name, value: d.id }))"
                @change="onDistrictChange"
                :trackBy="'value'"
                :label="'label'"
                :valueProp="'value'"
              />
            </div>

            <div class="flex items-center gap-2 mb-4">
              <input
                id="only_with_photo"
                type="checkbox"
                class="w-4 h-4"
                :checked="filterStore.activeFilters.only_with_photo"
                @change="onOnlyWithPhotoChange($event)"
              />
              <label for="only_with_photo" class="font-semibold select-none cursor-pointer">
                {{ $i18n?.locale === 'tk' ? 'Diňe suratlylar' : 'Только с фото' }}
              </label>
            </div>

            <SubmitBtn @onclick="applyResult" class="!w-full">
              {{ $t('action.filter') }}
              <span v-if="dynamicCount !== undefined">({{ dynamicCount }})</span>
            </SubmitBtn>

            <div class="my-3 py-3">
              <button
                @click="clearFilter"
                class="bg-accent-color w-full py-2 rounded-md text-white hover:bg-main-color"
              >
                {{ $t('page.clear') }}
              </button>
            </div>
          </template>
        </PublicationSide>
      </div>

      <div class="col-span-3 max-md:col-span-4">
        <div class="grid grid-cols-3 gap-4 max-lg:grid-cols-2">
          <router-link
            v-for="item in publications"
            :key="item.id"
            :to="{
              name: 'publicationDetail',
              params: { id: item.id },
              query: { category: currentCategory },
            }"
          >
            <PublicationCard
              :detail="item"
              :has_name="categoryDetail?.has_name"
              :has_price="categoryDetail?.has_price"
            />
          </router-link>

          <div v-if="!loading && publications.length === 0">
            <p class="text-xl font-medium whitespace-nowrap">
              {{ $t('page.no_publications') }}
            </p>
          </div>
        </div>

        <!-- ✅ Infinite scroll sentinel -->
        <div ref="infiniteTrigger" class="h-10" v-show="next && publications.length > 0"></div>

        <div v-if="loading" class="flex justify-center mt-10" role="status">
          <IconSpinner class="w-12 h-12 mr-2 text-gray-200 animate-spin fill-main-color" />
          <span class="sr-only">Loading...</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { useFilterStore } from '@/store/filterStore.js';

import PublicationCard from '@cmp/publication/PublicationCard.vue';
import PublicationSide from '@cmp/publication/PublicationSide.vue';
import SortButton from '@cmp/SortButton.vue';
import IconSpinner from '@icons/IconSpinner.vue';
import SubmitBtn from '@cmp/SubmitBtn.vue';

import Multiselect from '@vueform/multiselect';

import TextFilter from '@cmp/filterFields/TextFilter.vue';
import NumberFilter from '@cmp/filterFields/NumberFilter.vue';
import SwitchFilter from '@cmp/filterFields/SwitchFilter.vue';
import SelectFilter from '@cmp/filterFields/SelectFilter.vue';
import MultiSelectFilter from '@cmp/filterFields/MultiSelectFilter.vue';
import NestedSelectFilter from '@cmp/filterFields/NestedSelectFilter.vue';
import NestedMultiSelectFilter from '@cmp/filterFields/NestedMultiSelectFilter.vue';
import MultiNestedSelectFilter from '@cmp/filterFields/MultiNestedSelectFilter.vue';
import RangeFilter from '@cmp/filterFields/RangeFilter.vue';

import { SORT_OPTIONS } from '@/constants';

const filterStore = useFilterStore();
const route = useRoute();
const router = useRouter();
const infiniteTrigger = ref(null);
let io = null;
let publicationsRequestId = 0;

// --------------------
// State
// --------------------
const publications = ref([]);
const page = ref(1);
const next = ref(null);
const loading = ref(false);

const categoryDetail = ref({});
const ogProperties = ref([]);
const filtersReady = ref(false);

const currentDistrict = ref([]);
const districtsArray = ref([]);

const currentRelevancy = ref(null);
const keyVal = ref(0);

// --------------------
// Route-driven state
// --------------------
const getLocation = computed(() => {
  return JSON.parse(localStorage.getItem('selectedLocation')) || { id: 1 };
});

const currentCategory = ref(route.query.category !== 'null' ? route.query.category : null);
const currentSubscription = ref(route.query.subscription || null);
const isVip = ref(route.query.is_vip || null);
const currentSort = ref(route.query.sort_by || null);
const currentPublished = ref(route.query.published || null);

// --------------------
// Computed
// --------------------
const dynamicCount = computed(() => filterStore.dynamicCounts.__total__);

const publicationSideHideFilters = computed(() => {
  // если categoryDetail ещё не загрузился — не ломаемся
  const hasPrice = categoryDetail.value?.has_price;
  return hasPrice ? [] : ['currentPrice'];
});

// --------------------
// Lifecycle init
// --------------------
onMounted(async () => {
  // 1) мета для dynamic_count
  filterStore.setMeta({
    category: currentCategory.value,
    location: getLocation.value.id,
  });

  // 2) сортировка (для кнопки)
  currentRelevancy.value = SORT_OPTIONS.find(el => el.value == currentSort.value) || null;

  // 3) загрузка фильтров/категории/районов
  await Promise.all([getDistricts(), getCategoryDetail(), getProperties()]);

  // ✅ ВОТ ЭТО ДОБАВЬ: initial count для кнопки
  filterStore.fetchDynamicCount({ mode: 'include' });

  // 4) initial load publications
  await getPublications();
  await nextTick();
  setupInfinite();
});

// --------------------
// Watchers (IMPORTANT: не ломаем другие фильтры)
// subscription + vip должны применяться сразу по клику в сайдбаре (как у тебя было)
// --------------------
watch(
  () => route.query.subscription,
  async (newVal, oldVal) => {
    if (newVal !== oldVal) {
      currentSubscription.value = newVal || null;
      page.value = 1;
      next.value = null;
      publications.value = [];

      await getPublications();
      await nextTick();
      setupInfinite();
    }
  },
);

watch(
  () => route.query.is_vip,
  async (newVal, oldVal) => {
    if (newVal !== oldVal) {
      isVip.value = newVal || null;
      page.value = 1;
      next.value = null;
      publications.value = [];

      await getPublications();
      await nextTick();
      setupInfinite();
    }
  },
);

watch(
  () => route.query.category,
  async (newVal, oldVal) => {
    if (newVal !== oldVal) {
      currentCategory.value = newVal !== 'null' ? newVal : null;

      page.value = 1;
      next.value = null;
      publications.value = [];
      currentDistrict.value = [];

      // meta для dynamic count на новую категорию
      filterStore.setMeta({
        category: currentCategory.value,
        location: getLocation.value.id,
      });

      // сброс активных фильтров (чтобы другие типы фильтров не тащились между категориями)
      filterStore.resetFilters();

      await Promise.all([getCategoryDetail(), getProperties()]);

      // ✅ ДОБАВЬ
      filterStore.fetchDynamicCount({ mode: 'include' });

      await getPublications();
      await nextTick();
      setupInfinite();
    }
  },
);

watch(
  () => route.query.sort_by,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      currentSort.value = newVal || null;
      currentRelevancy.value = SORT_OPTIONS.find(el => el.value == currentSort.value) || null;
      // сортировку применяем через applyResult (чтобы поведение было одинаковым)
      applyResult();
    }
  },
);

watch(
  () => route.query.published,
  (newVal, oldVal) => {
    if (newVal !== oldVal) {
      currentPublished.value = newVal || null;
      // published ты обновляешь из PublicationSide через filterStore,
      // тут просто синхронизируем локальный ref (публикации НЕ грузим автоматически)
    }
  },
);

function onOnlyWithPhotoChange(e) {
  const checked = !!e?.target?.checked;

  filterStore.updateFilter({
    type: 'only_with_photo',
    value: checked,
  });

  // только dynamicCount, публикации по кнопке
  filterStore.fetchDynamicCount({ mode: 'include' });
}

// --------------------
// Components mapping
// --------------------
function getComponent(type, isRange = false) {
  if (isRange) return RangeFilter;

  switch (type) {
    case 0:
      return TextFilter;
    case 1:
    case 2:
      return NumberFilter;
    case 3:
      return SwitchFilter;
    case 4:
      return SelectFilter;
    case 5:
      return MultiSelectFilter;
    case 6:
      return NestedSelectFilter;
    case 7:
      return MultiNestedSelectFilter;
    case 8:
      return NestedMultiSelectFilter;
    default:
      return TextFilter;
  }
}

// --------------------
// District
// --------------------
function onDistrictChange(val) {
  const values = Array.isArray(val) ? val.map(v => (typeof v === 'object' ? v.value : v)) : [];
  currentDistrict.value = val;

  filterStore.updateFilter({
    type: 'district',
    value: values,
  });

  // ✅ только dynamicCount, без публикаций (не ломаем "по кнопке")
  filterStore.fetchDynamicCount({ mode: 'include' });
}

// --------------------
// API loaders
// --------------------
async function getDistricts() {
  try {
    const response = await api.get(`publications/districts/?location=${getLocation.value.id}`);
    districtsArray.value = response.data;
  } catch (e) {
    console.error(e);
  }
}

async function getCategoryDetail() {
  try {
    if (!currentCategory.value) {
      categoryDetail.value = {};
      return;
    }
    const response = await api.get(`publications/categories/${currentCategory.value}/`);
    categoryDetail.value = response.data;
  } catch (e) {
    console.error(e);
    categoryDetail.value = {};
  }
}

async function getProperties() {
  try {
    filtersReady.value = false;

    if (!currentCategory.value) {
      ogProperties.value = [];
      filtersReady.value = true;
      return;
    }

    const response = await api.get(
      `attributes/properties/?publication_categories=${currentCategory.value}`,
    );
    ogProperties.value = (response.data || []).filter(el => el.is_filter);

    filtersReady.value = true;
  } catch (e) {
    console.error(e);
    ogProperties.value = [];
    filtersReady.value = true;
  }
}

// --------------------
// Filter store glue
// --------------------
function getResultCount(val) {
  filterStore.updateFilter(val);
  filterStore.fetchDynamicCount({ mode: 'include' });
}

function setRangeFilter(val) {
  filterStore.updateFilter({
    type: 'range',
    property: val.property,
    value: [val.A, val.B],
  });
  filterStore.fetchDynamicCount({ mode: 'include' });
}

function teardownInfinite() {
  if (io) {
    io.disconnect();
    io = null;
  }
}

function setupInfinite() {
  teardownInfinite();

  io = new IntersectionObserver(
    entries => {
      const entry = entries[0];
      if (!entry?.isIntersecting) return;

      // ✅ защита от дублей
      if (loading.value) return;
      if (!next.value) return;

      loadMore();
    },
    {
      root: null,
      rootMargin: '250px', // чтобы подгружалось чуть заранее
      threshold: 0,
    },
  );

  if (infiniteTrigger.value) {
    io.observe(infiniteTrigger.value);
  }
}

onBeforeUnmount(() => {
  teardownInfinite();
});

// --------------------
// Publications load
// --------------------
async function getPublications({ append = false } = {}) {
  // Для infinite scroll оставляем защиту от дублей,
  // но не блокируем новый основной запрос при смене категории.
  if (append && loading.value) return;

  const requestId = ++publicationsRequestId;
  const requestedPage = page.value;

  loading.value = true;

  try {
    const params = {
      ...buildParamsFromFilters(),
      page: requestedPage,
      category: currentCategory.value,
      ordering: currentSort.value,
    };

    const response = await api.get('publications/publications/', {
      params,
      paramsSerializer: { indexes: null },
    });

    // Если уже ушёл более новый запрос — старый ответ игнорируем
    if (requestId !== publicationsRequestId) return;

    const results = response.data?.results || [];

    if (append) {
      publications.value = [...publications.value, ...results];
    } else {
      publications.value = results;
    }

    next.value = response.data?.next || null;
  } catch (e) {
    if (requestId === publicationsRequestId) {
      console.error(e);
    }
  } finally {
    // loading снимает только актуальный запрос
    if (requestId === publicationsRequestId) {
      loading.value = false;
    }
  }
}

async function loadMore() {
  if (loading.value) return;
  if (!next.value) return;

  page.value += 1;
  await getPublications({ append: true });
  await nextTick(); // ✅ важно при быстрых триггерах
}

async function applyResult() {
  page.value = 1;
  next.value = null;
  publications.value = [];
  await getPublications({ append: false });
  await nextTick();
  setupInfinite();
}

function buildParamsFromFilters() {
  const filters = filterStore.activeFilters;

  const params = {
    location: getLocation.value.id,

    // ✅ subscription/vip — из route-состояния (мгновенная фильтрация не ломается)
    subscription: currentSubscription.value,
    is_vip: isVip.value,

    // ✅ district — отдельным параметром
    district: Array.isArray(filters.district) ? filters.district : [],
  };

  if (filters.only_with_photo === true) {
    params.only_with_photo = true;
  }

  // multi_nested
  if (filters.multi_nested) {
    params.multi_nested = filters.multi_nested;
  }

  // published
  if (filters.published) {
    params.published = filters.published;
  }

  // range filters (dynamic + price=999 тоже здесь!)
  Object.entries(filters.range_values).forEach(([key, value]) => {
    if (!Array.isArray(value)) return;

    const A = value[0] ?? '';
    const B = value[1] ?? '';

    if (A === '' && B === '') return;

    params[`range_filter_${key}__range`] = `${A}__${B}`;
  });

  // switch
  Object.entries(filters.switch_values).forEach(([key, value]) => {
    if (value === true) {
      params[`switch_${key}`] = true;
    }
  });

  // text
  Object.entries(filters.text_values).forEach(([key, value]) => {
    if (value) {
      params[`text_${key}`] = value;
    }
  });

  // select_values (select/multi/nested/...)
  const selectValues = (filters.select_values || [])
    .map(obj => (typeof obj.value === 'object' && obj.value !== null ? obj.value.value : obj.value))
    .map(v => parseInt(v))
    .filter(v => !Number.isNaN(v));

  if (selectValues.length > 0) {
    params.select_values = selectValues;
  }

  return params;
}

function handleRelevancy(e) {
  currentSort.value = e;
  applyResult();
}

function clearFilter() {
  // ✅ очистить поле поиска в хедере
  window.dispatchEvent(new CustomEvent('publications:clear-search'));

  keyVal.value++;
  filterStore.resetFilters();

  publications.value = [];
  currentDistrict.value = [];

  // ✅ сброс query в URL, чтобы watcher'ы не вернули значения обратно
  router.replace({
    name: 'publications',
    query: {
      ...route.query,
      subscription: null,
      is_vip: null,
      sort_by: null,
      published: null,
    },
  });

  currentSubscription.value = null;
  isVip.value = null;
  currentSort.value = null;
  currentPublished.value = null;
  currentRelevancy.value = null;
  page.value = 1;
  next.value = null;

  applyResult();
}
</script>

<style scoped>
@import '@vueform/multiselect/themes/default.css';
</style>
